import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported, logEvent, setAnalyticsCollectionEnabled } from "firebase/analytics";

// Firebase web config is not secret, but it should still come from env so it can
// differ per environment. Falls back to the original project for local dev.
const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY            || "AIzaSyA1A2rAJgavfShUg511XvhGun9_jqneWQc",
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN        || "worldexplorer-dd78e.firebaseapp.com",
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID         || "worldexplorer-dd78e",
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET     || "worldexplorer-dd78e.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "321770861917",
  appId:             process.env.REACT_APP_FIREBASE_APP_ID             || "1:321770861917:web:b390d11e6f0505446524b4",
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID     || "G-FX7LQBSFQJ",
};

const app = initializeApp(firebaseConfig);

// Consent model: IMPLIED / OPT-OUT.
// Analytics initializes unless the user has explicitly declined. "No choice
// yet" (null) is treated as agreement, with the banner stating so. It only
// runs when:
//   1. REACT_APP_ENABLE_ANALYTICS === 'true' (build-time enable), and
//   2. localStorage 'orboot.analyticsConsent' !== 'denied' (no explicit opt-out).
// NOTE: this is an opt-out flow chosen for this product. For an under-13
// audience, COPPA/GDPR-K generally expect explicit opt-in (AUDIT.md §3) —
// flip initAnalyticsWithConsent back to `=== 'granted'` to require opt-in.
export let analytics = null;

// True only when analytics is built-in-enabled at build time. The consent
// banner uses this to decide whether asking is even meaningful.
export const ANALYTICS_BUILD_ENABLED =
  process.env.REACT_APP_ENABLE_ANALYTICS === 'true';

export const CONSENT_KEY = 'orboot.analyticsConsent';

// Returns 'granted', 'denied', or null (no choice recorded yet).
export function getAnalyticsConsent() {
  try {
    return window.localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

// Records explicit agreement and ensures analytics is running.
export async function grantAnalyticsConsent() {
  try {
    window.localStorage.setItem(CONSENT_KEY, 'granted');
  } catch {
    /* storage unavailable — analytics simply won't persist consent */
  }
  const a = await initAnalyticsWithConsent();
  if (a) {
    try { setAnalyticsCollectionEnabled(a, true); } catch { /* no-op */ }
  }
  return a;
}

// Records an explicit opt-out. Because this is an opt-out flow, analytics may
// already have started on page load — so we also turn collection OFF.
export function denyAnalyticsConsent() {
  try {
    window.localStorage.setItem(CONSENT_KEY, 'denied');
  } catch {
    /* no-op */
  }
  if (analytics) {
    try { setAnalyticsCollectionEnabled(analytics, false); } catch { /* no-op */ }
  }
}

export async function initAnalyticsWithConsent() {
  if (analytics) return analytics;
  if (!ANALYTICS_BUILD_ENABLED) return null;
  // Opt-out model: run unless the user has explicitly declined.
  if (getAnalyticsConsent() === 'denied') return null;

  try {
    if (await isSupported()) {
      analytics = getAnalytics(app);
    }
  } catch {
    analytics = null;
  }
  return analytics;
}

/**
 * Safe event logger. No-ops until analytics has been initialized, and (under
 * the opt-out model) goes quiet if the user declines, since that disables
 * collection. Call it freely from anywhere — it will never throw.
 */
export function trackEvent(name, params) {
  if (!analytics) return;
  try {
    logEvent(analytics, name, params);
  } catch {
    /* analytics errors must never break the app */
  }
}

export default app;
